import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import strings from "translate/language";
import { useDispatch, useSelector } from "react-redux";
import { getScheduledFollowupList } from "reduxStore/action/followUpActions";
import { getFollowupStatus } from "reduxStore/action/masterActions";
import { handleListResponse, seo } from "utils/utils";
import TableDataNotFound from "component/TableDataNotFound";
import moment from "moment";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import Pagination from "component/common/Pagination";
import usePage from "utils/usePage";
import { getMarkScheduledFollowup } from "../../reduxStore/action/followUpActions";
import { toast } from "react-toastify";

const ScheduledActivityFollowup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [followupListData, setFollowupListData] = useState([]);
  const [page, setPage] = usePage();
  const [followupLeadData, setFollowupLeadData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [closing_remark, setClosingRemark] = useState("");

  const getDefaultDateRange = () => {
    const today = moment().format("YYYY-MM-DD");
    // const lastMonth = moment().subtract("days").format("YYYY-MM-DD");
    return { start_date: today, end_date: today };
  };

  const defaultDates = getDefaultDateRange();

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    limit: "",
    offset: "",
    status: true,
    due: false,
  });

  const followupListSelector = useSelector((state) => state?.followUp) || [];

  const { view, edit, isAdmin } = usePermission({
    view: "view_follow",
    edit: "edit_follow",
  });

  const navigatpage = async (navname) => {
    navigate(navname);
  };

  useEffect(() => {
    if (form.status !== undefined) {
      const payload = {
        ...form,
        offset: 0,
        limit: 10,
      };
      dispatch(getScheduledFollowupList(payload));
    }
    // dispatch(getScheduledFollowupList({ limit: 10, offset: 0, status: true }));
  }, []);

  useEffect(() => {
    if (followupListSelector?.response?.status === 200) {
      const data = followupListSelector?.response?.data || [];
      setFollowupListData(handleListResponse(data) ? data : []);
    } else {
      setFollowupListData([]);
    }
  }, [followupListSelector]);

  useEffect(() => {
    dispatch(getFollowupStatus());
  }, []);

  // Handle pagination
  const handlePageClick = (newOffset) => {
    const { start_date, end_date } =
      form.start_date && form.end_date ? form : getDefaultDateRange();

    setPage(newOffset);
    if (!form.due) {
      delete form.due;
    }
    dispatch(
      getScheduledFollowupList({
        ...form,
        start_date: start_date,
        end_date: end_date,
        offset: newOffset,
        limit: 10,
      })
    );
  };

  // Form validation
  const handleValid = () => {
    let errors = { ...formValid };
    let formError = false;

    if (form.start_date && !form.end_date) {
      formError = true;
      errors["end_date"] = strings.endDateErr;
    }

    setFormValid(errors);
    return formError;
  };

  // Search filter
  const filterDetail = () => {
    if (!handleValid()) {
      if (!form.due) {
        delete form.due;
      }
      dispatch(
        getScheduledFollowupList({
          ...form,
          offset: 0,
          limit: 10,
          status: form.status,
        })
      );
      setPage(0);
    }
  };

  // Reset filter
  const filterReset = () => {
    // const { start_date, end_date } = getDefaultDateRange();
    const resetForm = {
      start_date: "",
      end_date: "",
      status: true,
      limit: 10,
      offset: 0,
    };
    setForm(resetForm);
    setFormValid({});
    if (!resetForm.due) {
      delete resetForm.due;
    }
    dispatch(getScheduledFollowupList({ ...resetForm }));
    setPage(0);
  };

  // Handle input change
  const handleInputField = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      value === "true" ? true : value === "false" ? false : value;

    setForm((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
    if (name === "end_date") {
      setFormValid((prev) => ({ ...prev, end_date: "" }));
    }
  };

  // Enable/disable end_date
  useEffect(() => {
    setEndDateDisable(!form.start_date);
  }, [form.start_date]);

  // SEO setup
  useEffect(() => {
    seo({
      title: "Scheduled Followup Listing",
      metaDescription: "Scheduled Followup listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleOpenModal = (item) => {
    setSelectedFollowup(item);
    setClosingRemark("");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFollowup(null);
    setClosingRemark("");
  };

  const handleUpdateStatus = () => {
    if (!closing_remark.trim()) {
      alert("Please enter a closing_remark before updating the status.");
      return;
    }
    if (!selectedFollowup?._id) {
      console.error("No follow-up selected for status update.");
      return;
    }
    const payload = {
      _id: selectedFollowup._id,
      closing_remark: closing_remark.trim(),
    };

    dispatch(getMarkScheduledFollowup(payload)).then(() => {
      toast.success("Follow-up status updated successfully!");
      // refreshFollowupList();
      refreshFollowupList();
      handleCloseModal();
    });
  };

  const refreshFollowupList = () => {
    const payload = {
      ...form,
      offset: page,
      limit: 10,
      status: form.status,
    };
    if (!form.due) {
      delete payload.due;
    }
    dispatch(getScheduledFollowupList(payload));
  };

  return (
    <div className="page-body">
      <Breadcrumb
        title={strings.scheduledFollowup}
        titleactive={strings.scheduledFollowupList}
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12 col-xl-12">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <form className="theme-form">
                      <div className="row">
                        <div className="col-md-4">
                          <span className="cardText">{strings.startDate}</span>
                          <div
                            className="input-group date"
                            id="dt-date"
                            data-target-input="nearest"
                          >
                            <input
                              className="form-control datetimepicker-input digits"
                              type="date"
                              data-target="#dt-date"
                              name="start_date"
                              value={form.start_date}
                              onChange={handleInputField}
                              // min={form.start_date}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">{strings.endDate}</span>
                          <div
                            className="input-group date"
                            id="dt-date"
                            data-target-input="nearest"
                          >
                            <input
                              className="form-control datetimepicker-input digits"
                              type="date"
                              name="end_date"
                              value={form.end_date}
                              onChange={handleInputField}
                              min={form.start_date}
                              // max={form.end_date}
                              disabled={endDateDisable}
                            />
                          </div>
                          {formValid.end_date && (
                            <span style={{ color: "red" }}>
                              {formValid.end_date}
                            </span>
                          )}
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">
                            {strings.searchByType}
                          </span>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="status"
                            value={form.status}
                            onChange={handleInputField}
                          >
                            <option value={true}>Active</option>
                            <option value={false}>Closed</option>
                          </select>
                        </div>
                      </div>

                      <div className="row pt-3">
                        {/* <div className="col-md-4">
                          <span className="cardText">
                            {strings.searchByStatus}
                          </span>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="followup_status"
                            value={form.followup_status}
                            onChange={handleInputField}
                          >
                            <option value={""}>{strings.selectstatus}</option>
                            {followupLeadData?.length ? (
                              followupLeadData.map((item, index) => (
                                <option key={index} value={item._id}>
                                  {item.title}
                                </option>
                              ))
                            ) : (
                              <option>{strings.Loading}</option>
                            )}
                          </select>
                        </div> */}
                        <div className="col-md-4 pt-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="dueCheck"
                              name="due"
                              checked={form.due}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  due: e.target.checked,
                                }))
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="dueCheck"
                            >
                              {strings.dueFollowUps || "Due Followups"}
                            </label>
                          </div>
                        </div>
                        <div className="col-md-8 pt-4">
                          <div className="form-group d-flex justify-content-end">
                            <button
                              className="btn btn-warning mr-4"
                              type="button"
                              onClick={filterDetail}
                            >
                              {strings.search}
                            </button>
                            <button
                              className="btn btn-dark"
                              type="button"
                              onClick={filterReset}
                            >
                              {strings.reset}
                            </button>
                          </div>
                        </div>
                      </div>

                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-xl-12">
                <div className="card">
                  <div className="card-header">
                    <h5>{strings.scheduledlistOfFollowup}</h5>
                  </div>
                  <div
                    className="table-responsive"
                    style={{ overflowX: "auto" }}
                  >
                    <table className="table table-bordered table-sm table-striped">
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          textAlign: "center",
                        }}
                      >
                        <tr>
                          <th style={{ minWidth: "160px" }}>
                            {strings.createdDate}
                          </th>
                          <th style={{ minWidth: "180px" }}>
                            {strings.followUPDateTime}
                          </th>
                          <th style={{ minWidth: "140px" ,textAlign:"left"}}>
                            {strings.visitorName}
                          </th>
                          <th style={{ minWidth: "100px" }}>
                            {strings.visitingScore}
                          </th>
                          <th style={{ minWidth: "200px" ,textAlign:"left"}}>
                            {strings.followupRemark}
                          </th>
                          <th style={{ minWidth: "160px" ,textAlign:"left" }}>
                            {form.status === true
                              ? strings.status
                              : strings.closingRemark}
                          </th>
                          {form.status === false && (
                            <th style={{ minWidth: "200px"}}>
                              {strings.closingDateTime}
                            </th>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {followupListData?.length ? (
                          followupListData.map((item, index) => {
                            const {
                              followup_date,
                              next_followup_date,
                              followup_time,
                              customer_first_name,
                              visit_score,
                              remark,
                              closing_remark,
                              closing_date,
                              closing_time,
                              is_due,
                            } = item;

                            return (
                              <tr className="text-center" key={index}>
                                <td>
                                  {followup_date
                                    ? moment(followup_date).format(
                                        "DD-MM-YYYY hh:mm A"
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {next_followup_date ? (
                                    <span
                                      style={{
                                        color: is_due ? "red" : "inherit",
                                      }}
                                    >
                                      {moment(next_followup_date).format(
                                        "DD-MM-YYYY"
                                      )}{" "}
                                      {followup_time || "-"}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td className="text-left">{customer_first_name || "-"}</td>
                                <td>{visit_score || "0"}</td>
                                <td className="text-left">{remark || "N/A"}</td>

                                {form.status === true ? (
                                  <td className="text-left">
                                    <button
                                      className="btn-sm btn-primary"
                                      onClick={() => handleOpenModal(item)}
                                    >
                                      {strings.update || "Update"}
                                    </button>
                                  </td>
                                ) : (
                                  <td className="text-left">{closing_remark || "N/A"}</td>
                                )}

                                {form.status === false && (
                                  <td>
                                    {closing_date && closing_time
                                      ? `${moment(closing_date).format(
                                          "DD-MM-YYYY"
                                        )} ${closing_time}`
                                      : "N/A"}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={form.status === false ? "8" : "7"}
                              className="text-center"
                            >
                              <TableDataNotFound data={followupListData} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* <Pagination
                    totalCount={followupListSelector?.response?.total_data}
                    listData={followupListData}
                    onPageChange={handlePageClick}
                    itemOffset={page}
                    setItemOffset={setPage}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Status</h5>
                <button
                  type="button"
                  className="close"
                  onClick={handleCloseModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="closing_remark">Closing Remark</label>
                  <textarea
                    id="closing_remark"
                    className="form-control"
                    rows="4"
                    value={closing_remark}
                    onChange={(e) => setClosingRemark(e.target.value)}
                    placeholder="Enter your comments here"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleUpdateStatus}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ScheduledActivityFollowup;
